<template>
  <header class="toolbar col-12 d-flex justify-content-center flex-wrap">
    <flex-layout class="col-12 bg-primary" justify="center" align="center" wrap>
      <flex-layout class="col-12 col-md-auto pe-1 py-2 py-md-0" justify="center" align="center">
        <base-image
          class="mt-1 clickable header-position"
          src="housedo-logo-white.svg"
          alt="HOUSE DO"
          :max-width="140"
          @click="router.push({name: 'News', })"
          @keyup.enter="router.push({name: 'News', })"
        />
        <base-title
          class="ms-4 title clickable"
          color="white"
          border
          size="xs"
          @click="router.push({name: 'News', })">
          マイページ
        </base-title>
      </flex-layout>
      <flex-layout class="col-12 col-md-auto ps-1" justify="center">
        <toolbar-button
          v-for="(item, i) in menuItems"
          :key="i"
          :icon="item.icon"
          :label="item.text"
          @click="onMenuItemClick(item)"/>
      </flex-layout>
    </flex-layout>
    <div v-if="breakpoint.mdAndUp" class="w-100 sub-menu bg-white">
      <div class="sub-menu-wrapper">
        <div ref="subMenuFlex" class="sub-menu-flex">
        <flex-layout justify="center" align="center">
        <toolbar-sub-button
          v-for="(item, i) in selectedSubMenus"
          :key="i"
          :label="item.text"
          @click="onSubItemClick(item)"
          :route="typeof item.route === 'string' ? item.route : undefined"
          />
      </flex-layout>
        </div>
      </div>
    </div>
    <div v-else>
      <SlidingUpMenu :model-value="selectedSubMenus" @update:model-value="close" @click="onSubItemClick"/>
    </div>
  </header>
</template>

<script setup lang="ts">
import BaseImage from '@/components/Images/BaseImage.vue';
import ToolbarButton from '@/components/Toolbar/ToolbarButton.vue';
import FlexLayout from '@/components/Layouts/FlexLayout.vue';
import ToolbarSubButton from '@/components/Toolbar/ToolbarSubButton.vue';
import {useRouter} from 'vue-router';
import {computed, onMounted, ref, watch, nextTick} from 'vue';
import {useAuthLogout} from '@/composable/controller/auth/useAuthLogout';
import useGetShopConfig from '@/composable/controller/shop/useGetShopConfig';
import {useBreakpoint} from '@/composable/ui/useScreen';
import useApplicationConfig from "@/composable/controller/application/useApplicationConfig";
import BaseTitle from "@/components/Texts/BaseTitle.vue";
import SlidingUpMenu from "@/components/Layouts/SlidingUpMenu.vue";
import useGetMyHopes from "@/composable/controller/hope/useGetMyHopes";

const {isOnlineInquiryAvailable} = useGetShopConfig();
const {hopes} = useGetMyHopes();

const router = useRouter();
const breakpoint = useBreakpoint();
const {configuration} = useApplicationConfig();
const {request: logout} = useAuthLogout();

type MenuItem = {
  text: string,
  icon: string,
  subMenuItems: { text: string, route: string | Function, tab?: string }[]
};

const selectedSubMenus = ref<MenuItem['subMenuItems']>([]);
const subMenuFlex = ref<HTMLElement | null>(null);

const menuItems = computed(() => {
  const menuItems: MenuItem[] = [];
  const sellMenuItem = {
    text: '買いたい',
    icon: 'toolbar-buy',
    subMenuItems: [
      {text: '不動産検索', route: 'EstateList'},
      {text: "イベント情報", route: "EventIndex"},
      { text: 'おうちノウハウ講座', route: 'KnowHowBuy'}
    ]
  };
  if (configuration.value.isInDataPersistenceMaintenance && configuration.value.isDoNetOnlyMaintenance) {
    sellMenuItem.subMenuItems.splice(1, 0, {text: 'お気に入り', route: 'EstateFavoriteList'});
    sellMenuItem.subMenuItems.splice(2, 0, {text: '最近見た物件', route: 'EstateHistoryList'});
  } else if(!configuration.value.isInDataPersistenceMaintenance) {
    sellMenuItem.subMenuItems.splice(1, 0, {text: 'お気に入り', route: 'EstateFavoriteList'});
    sellMenuItem.subMenuItems.splice(2, 0, {text: '最近見た物件', route: 'EstateHistoryList'});
    sellMenuItem.subMenuItems.push({text: 'ご利用店舗', route: 'Hope'});
  }

  menuItems.push(sellMenuItem);
  menuItems.push({
    text: '売りたい',
    icon: 'toolbar-sale',
    subMenuItems: [
      {text: '10秒AI査定', route: 'EstimateEstatePrice',},
      {text: '査定保存確認', route: 'EstatesToSell'},
      {text: 'ご依頼の物件', route: 'SubmittedEstatesToSell',},
      { text: 'おうちノウハウ講座', route: 'KnowHowSell'}
    ]
  });
  menuItems.push({
    text: 'アカウント',
    icon: 'user',
    subMenuItems: [
      {text: 'アカウント', route: 'AccountIndex',},
      {text: 'ログアウト', route: logout,}
    ]
  });
  if (hopes.value.length) {
    const inquiryOption = {
      text: "お問合せ",
      icon: 'conversation',
      subMenuItems: [
        {text: '来店予約', route: 'InquiryContact'},
        {text: 'お問合せ', route: 'InquiryOther'},
        {text: '使い方', route: 'UsersGuide'}
      ],
    };
    if (isOnlineInquiryAvailable.value) {
      inquiryOption.subMenuItems.splice(1, 0, {
        text: 'オンライン来店予約',
        route: 'InquiryOnline'
      });
    }
    menuItems.push(inquiryOption);
  }
  return menuItems;
});

function onMenuItemClick(item: MenuItem) {
  selectedSubMenus.value = item.subMenuItems;
}

function onSubItemClick(item: MenuItem['subMenuItems'][number]) {
  if (breakpoint.smAndDown) selectedSubMenus.value = [];

  if (typeof item.route === 'function') item.route();
  else router.push({name: item.route});

}


function close(value: any) {
  selectedSubMenus.value = value;
}

function selectCorrectMenuItem() {
  if (breakpoint.smAndDown) {
    selectedSubMenus.value = [];
    return;
  }
  const currentRouteName = router.currentRoute.value.name;
  const parent = router.currentRoute.value.meta?.parent as string | undefined;
  for (const menuItem of menuItems.value) {
    const hasCurrentRoute = menuItem.subMenuItems.some(subItem => {
      if (typeof subItem.route === 'string') {
        const matches = subItem.route === currentRouteName || (parent && subItem.route === parent);
        return matches;
      }
      return false;
    });
    if (hasCurrentRoute) {
      onMenuItemClick(menuItem);
      return;
    }
  }
  if (menuItems.value.length > 0) {
    onMenuItemClick(menuItems.value[0]);
  }
}

watch(() => menuItems.value, (newMenuItems) => {
  selectCorrectMenuItem();
}, { immediate: true, deep: true });

watch(() => router.currentRoute.value.name, () => {
  selectCorrectMenuItem();
});

watch(() => breakpoint.smAndDown, (isMobile) => {
  if (isMobile) {
    selectedSubMenus.value = [];
  } else {
    selectCorrectMenuItem();
  }
});

onMounted(() => {
  nextTick(() => {
    selectCorrectMenuItem();
  });
});

</script>

<style scoped>
.toolbar {
  position: -webkit-sticky;
  position: sticky;
  top: 0;
  left: 0;
  right: 0;
  z-index: 20;
}
.sub-menu-flex {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 44px;
}

.title {
  transition: .3s ease-in-out;
  box-shadow: inset 0 0 0 1px var(--offWhite);
  font-size: 16px !important;
}
.sub-menu {
  filter: drop-shadow(0px 3px 6px rgba(0, 0, 0, .059));
}

.clickable {
  cursor: pointer;
}

.header-position {
  position: relative;
  top: 1px;
}

html, body {
  max-width: 100%;
  overflow-x: hidden;
}

@media (max-width: 767px) {
  .toolbar .col-12 {
    padding-left: 0;
    padding-right: 0;
  }
  .sub-menu {
    padding: 0;
  }
}
</style>
